import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { addTimecardRecord, adjustTimeCardApi } from "../../../redux/features/EmployeeTimeCard";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import {
    fetchAttendanceTags,
    fetchAttendanceTagsV2,
    getDepartments,
    getJobCodes,
    getSites,
    getSitesV2,
} from "../../../redux/features/common";
import {
    AddTimecardPayload,
    AdjustTimeCardPayload,
    BreakDuration,
    SelectedTag,
    SelectedTags,
} from "../../../types/state/timecards";
import {
    convertToDateTimeLocalFormat,
    convertToFullDateTime,
    formatDurationStartEndDate,
    convertDecimalHoursToHM,
} from "../../../utils/employeeTimeCardhelper";
import Select from "react-select";
import { ArrowLeft, CircleX, Loader, Plus } from "lucide-react";
import { setShowTopNav, setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import AttendanceTagSelector from "./AttendanceTagSelector";
import BreakManagerOverlay from "./BreakManagerOverlay";
import { AttendanceTags } from "../../../types";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { DateTimeUtil } from "../../../utils/dateTime";
import { NgwAvatar } from "../../common";
import { isPrivilege, UserPrivileges } from "../../../rules";

type AttendanceTagDateEligibility = "weekend" | "weekday";

/**
 * Formats decimal hours to HH:MM format for time input (with leading zeros)
 * @param hours Decimal hours (e.g., 3.5)
 * @returns Formatted string (e.g., "03:30")
 */
const formatHoursToTimeString = (hours: number): string => {
    // Handle edge cases
    if (isNaN(hours) || hours < 0) {
        return "00:00";
    }

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    // Handle the case where rounding minutes results in 60
    if (minutes === 60) {
        return `${String(wholeHours + 1).padStart(2, "0")}:00`;
    }

    return `${String(wholeHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/**
 * Formats a Date into a string suitable for date or datetime-local inputs
 * @param date Date object to format
 * @param includeTime Whether to include time (for datetime-local)
 * @returns Formatted string like "2025-11-14" or "2025-11-14T10:30"
 */
const formatLocalInputValue = (date: Date, includeTime: boolean = false): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return "";
    }

    // Adjust for timezone offset so the string reflects local time
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(date.getTime() - tzOffsetMs).toISOString();

    return includeTime ? localISOTime.slice(0, 16) : localISOTime.slice(0, 10);
};

const isRootAttendanceTag = (field: AttendanceTags): boolean =>
    field.parent_labor_dimension_id === null || field.parent_labor_dimension_id === "";

const formatPayPeriodDisplay = (start: string, end: string): string => {
    const prettyRange = DateTimeUtil.formatDateRangeFancy(start, end, false);
    const startYear = new Date(start).getFullYear();
    const endYear = new Date(end).getFullYear();

    if (Number.isNaN(startYear) || Number.isNaN(endYear)) {
        return prettyRange;
    }

    return startYear === endYear ? `${prettyRange}, ${startYear}` : `${prettyRange} • ${startYear}/${endYear}`;
};

const parseSelectedDateValue = (value: string): Date | null => {
    if (!value) {
        return null;
    }

    const normalizedValue = value.includes("T") ? value : `${value}T12:00:00`;
    const parsedDate = new Date(normalizedValue);

    return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getDateEligibilityType = (value: string): AttendanceTagDateEligibility | null => {
    const parsedDate = parseSelectedDateValue(value);
    if (!parsedDate) {
        return null;
    }

    const dayOfWeek = parsedDate.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6 ? "weekend" : "weekday";
};

const isAttendanceTagEligibleForSelectedDates = (
    field: AttendanceTags,
    selectedDateEligibility: AttendanceTagDateEligibility[]
): boolean => {
    const eligibleWhen = field.other_info_json?.eligible_when;

    if (eligibleWhen?.field !== "selected_date") {
        return true;
    }

    const eligibleValues =
        eligibleWhen.value?.filter(
            (value): value is AttendanceTagDateEligibility => value === "weekend" || value === "weekday"
        ) || [];

    if (eligibleValues.length === 0) {
        return true;
    }

    if (selectedDateEligibility.length === 0) {
        return false;
    }

    return eligibleValues.some((value) => selectedDateEligibility.includes(value));
};

/**
 * Filters break properties based on their type:
 * - Duration-based breaks: include only type and duration_minutes
 * - Time-based breaks: include only type, start_datetime, and end_datetime
 * @param breaks Array of break objects
 * @returns Array of breaks with only the relevant properties for each type
 */
const filterBreaksByType = (breaks: BreakDuration[]): BreakDuration[] => {
    return breaks.map((breakItem) => {
        // First check which type of break this is based on available properties
        if (breakItem.start_datetime && breakItem.end_datetime) {
            // This is a time-based break - use only type, start_datetime and end_datetime
            return {
                type: breakItem.type,
                start_datetime: breakItem.start_datetime,
                end_datetime: breakItem.end_datetime,
            };
        } else if (breakItem.duration_minutes !== undefined) {
            // This is a duration-based break - use only type and duration_minutes
            return {
                type: breakItem.type,
                duration_minutes: breakItem.duration_minutes,
            };
        }

        // If it's neither, return as is (shouldn't happen in normal operation)
        return breakItem;
    });
};

/**
 * Calculates the break duration in minutes from start and end datetimes
 * @param startDatetime Start datetime string in ISO format
 * @param endDatetime End datetime string in ISO format
 * @returns Duration in minutes, capped between 1 and 480 (8 hours)
 */
const calculateDurationMinutes = (startDatetime: string, endDatetime: string): number => {
    if (!startDatetime || !endDatetime) return 0;

    const startMs = new Date(startDatetime).getTime();
    const endMs = new Date(endDatetime).getTime();

    if (isNaN(startMs) || isNaN(endMs) || endMs <= startMs) return 0;

    const durationMs = endMs - startMs;
    return Math.max(1, Math.min(480, Math.round(durationMs / 60000)));
};

/**
 * Parses a time input string and converts it to decimal hours
 * @param timeValue Input string, either "HH:MM" format or a plain number
 * @returns Decimal hours or 0 if input is empty
 */
const parseTimeInput = (timeValue: string): number => {
    if (!timeValue) {
        return 0;
    }

    if (timeValue.includes(":")) {
        // Time format (HH:MM)
        const [hours, minutes] = timeValue.split(":");
        const hoursNum = parseInt(hours) || 0;
        const minutesNum = parseInt(minutes) || 0;

        return hoursNum + minutesNum / 60;
    } else {
        // Plain number format
        return Number(timeValue) || 0;
    }
};

interface LocationState {
    recordData?: any;
    mode?: "create" | "edit";
    recordId?: string;
    isManager?: boolean;
    managerView?: {
        department?: string;
        departmentCode?: string;
    };
    managerReturnPath?: string;
    employeeId?: string;
    employeeName?: string;
    payPeriod?: string;
}

const AddTimeSheetEntryComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { setSourcePath, getSourcePath } = useNavigationHistory();
    const locationState = location.state as LocationState | null;

    // Form States
    const [startDateTime, setStartDateTime] = useState("");
    const [endDateTime, setEndDateTime] = useState("");
    const [site, setSite] = useState("");
    const [department, setDepartment] = useState("");
    const [duration, setDuration] = useState(0);
    const [note, setNote] = useState("");
    const [jobCode, setJobCode] = useState("");
    const [isHoursEntry, setIsHoursEntry] = useState(false);
    const [hoursEntries, setHoursEntries] = useState<{ date: string; hours: number }[]>([{ date: "", hours: 0 }]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [breaks, setBreaks] = useState<BreakDuration[]>([]);

    const [fieldStack, setFieldStack] = useState<(AttendanceTags | null)[]>([]);
    const [localSelectedMap, setLocalSelectedMap] = useState<
        Record<string, { value_code: string; value_text: string }[]>
    >({});

    const [activeTagField, setActiveTagField] = useState<AttendanceTags | null>(null);
    const [showBottomSheet, setShowBottomSheet] = useState(false);

    // State for break manager overlay
    const [showBreakManager, setShowBreakManager] = useState(false);
    const [editBreakIndex, setEditBreakIndex] = useState<number | null>(null);

    // Redux States
    const user = useSelector((state: RootState) => state.user?.user);
    const { groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const { employeeRecords } = useSelector((state: RootState) => state.timesheet);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);
    const attendanceOption = user?.mobile_timeclock_option_config?.ATTENDANCE_OPTION;
    const allowFutureTimesheetEntries = Boolean(user?.MOBILE_TIMECARD_HOUR_ENTRY?.ALLOW_FUTURE_TIMESHEET_ENTRIES);
    const shouldRestrictToTodayOnly = !allowFutureTimesheetEntries;
    const isAdmin = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
            }),
        [user?.employee_access_role_privileges]
    );
    const isAllowedToEdit = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges]
    );
    const isManagerPrivilege = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges]
    );
    const isManager = useMemo(() => user?.is_manager, [user?.is_manager]);
    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;
    const canEdit = isManager ? managerPrivilege : selfPrivilege;
    const todayReferenceDate = useMemo(() => new Date(), []);
    const endOfTodayDate = useMemo(() => {
        const endOfToday = new Date(todayReferenceDate);
        endOfToday.setHours(23, 59, 59, 999);
        return endOfToday;
    }, [todayReferenceDate]);
    const maxDateTimeInputValue = useMemo(
        () => formatLocalInputValue(endOfTodayDate, true),
        [endOfTodayDate]
    );
    const maxDateInputValue = useMemo(
        () => formatLocalInputValue(todayReferenceDate, false),
        [todayReferenceDate]
    );

    // const siteOptions = useSelector((state: RootState) => state.common.sites);
    const siteOptions = useSelector((state: RootState) => state.common.sitesV2);
    const jobCodeOptions = useSelector((state: RootState) => state.common.jobCodes);
    const attendanceTagsOptions = useSelector((state: RootState) => state.common.attendanceTagsV2);
    const rootAttendanceTags = useMemo(
        () => attendanceTagsOptions.filter((field) => isRootAttendanceTag(field)),
        [attendanceTagsOptions]
    );
    const selectedDateEligibility = useMemo<AttendanceTagDateEligibility[]>(() => {
        const eligibilityTypes = new Set<AttendanceTagDateEligibility>();

        if (isHoursEntry) {
            hoursEntries.forEach((entry) => {
                const eligibilityType = getDateEligibilityType(entry.date);
                if (eligibilityType) {
                    eligibilityTypes.add(eligibilityType);
                }
            });

            return Array.from(eligibilityTypes);
        }

        const eligibilityType = getDateEligibilityType(startDateTime);
        return eligibilityType ? [eligibilityType] : [];
    }, [hoursEntries, isHoursEntry, startDateTime]);
    const eligibleAttendanceTags = useMemo(
        () => {
            const fieldsById = attendanceTagsOptions.reduce((acc, field) => {
                acc[String(field.labor_dimensions_id)] = field;
                return acc;
            }, {} as Record<string, AttendanceTags>);

            return attendanceTagsOptions.filter((field) => {
                if (!isAttendanceTagEligibleForSelectedDates(field, selectedDateEligibility)) {
                    return false;
                }

                let parentId = field.parent_labor_dimension_id ? String(field.parent_labor_dimension_id) : null;

                while (parentId) {
                    const parentField = fieldsById[parentId];

                    if (!parentField) {
                        return false;
                    }

                    if (!isAttendanceTagEligibleForSelectedDates(parentField, selectedDateEligibility)) {
                        return false;
                    }

                    parentId = parentField.parent_labor_dimension_id
                        ? String(parentField.parent_labor_dimension_id)
                        : null;
                }

                return true;
            });
        },
        [attendanceTagsOptions, selectedDateEligibility]
    );
    const visibleRootAttendanceTags = useMemo(
        () => eligibleAttendanceTags.filter((field) => isRootAttendanceTag(field)),
        [eligibleAttendanceTags]
    );

    // useEffect(() => {
    //     dispatch(getSitesV2({
    //         employee_id: locationState?.employeeId ?? '',
    //     }));
    //     dispatch(fetchAttendanceTagsV2({
    //         employee_id: locationState?.employeeId ?? '',
    //     }));
    //     dispatch(getJobCodes());
    //     dispatch(getDepartments());
    // }, [dispatch]);

    const canAddMore = hoursEntries.every((entry) => entry.date.trim() !== "" && entry.hours > 0);
    const jobCodeSelectOptions = jobCodeOptions.map((job) => ({
        value: job.work_code_number,
        label: `${job.work_code_number} - ${job.work_code_name}`,
    }));

    const siteSelectOptions = siteOptions.map((site) => ({
        value: site.us_site_code,
        label: site.user_site_name,
    }));

    const departmentOptions = useSelector((state: RootState) => state.common.departments);
    const departmentSelectOptions = departmentOptions.map((dept: any) => ({
        value: dept.department_code,
        label: dept.department_name,
    }));
    const employeeRecordMatch = useMemo(() => {
        if (!locationState?.employeeId) {
            return undefined;
        }

        return employeeRecords?.find(
            (employee) => String(employee.emp_detail?.employee_id) === String(locationState.employeeId)
        );
    }, [employeeRecords, locationState?.employeeId]);
    const employeeDisplayName = useMemo(() => {
        return (
            locationState?.employeeName ||
            employeeRecordMatch?.emp_detail?.emp_name ||
            groupedTimesheets?.data?.emp_name ||
            locationState?.recordData?.emp_name ||
            [user?.os_emp_first_name, user?.os_emp_last_name].filter(Boolean).join(" ").trim() ||
            "Employee"
        );
    }, [
        employeeRecordMatch?.emp_detail?.emp_name,
        groupedTimesheets?.data?.emp_name,
        locationState?.employeeName,
        locationState?.recordData?.emp_name,
        user?.os_emp_first_name,
        user?.os_emp_last_name,
    ]);
    const employeeImageUrl = useMemo(() => {
        return (
            employeeRecordMatch?.emp_detail?.profile_image_url ||
            groupedTimesheets?.data?.image_url ||
            locationState?.recordData?.profile_image_url ||
            user?.os_emp_image_name ||
            null
        );
    }, [
        employeeRecordMatch?.emp_detail?.profile_image_url,
        groupedTimesheets?.data?.image_url,
        locationState?.recordData?.profile_image_url,
        user?.os_emp_image_name,
    ]);
    const departmentDisplay = useMemo(() => {
        const departmentCode =
            locationState?.managerView?.departmentCode ||
            employeeRecordMatch?.department?.department_code ||
            employeeRecordMatch?.department?.code ||
            locationState?.recordData?.department?.code ||
            user?.department_code ||
            "";
        const resolvedDepartmentName =
            locationState?.managerView?.department ||
            employeeRecordMatch?.department?.department_name ||
            employeeRecordMatch?.department?.name ||
            locationState?.recordData?.department?.name ||
            departmentOptions.find((dept: any) => String(dept.department_code) === String(departmentCode))
                ?.department_name;

        return resolvedDepartmentName || departmentCode;
    }, [
        departmentOptions,
        employeeRecordMatch?.department?.code,
        employeeRecordMatch?.department?.department_code,
        employeeRecordMatch?.department?.department_name,
        employeeRecordMatch?.department?.name,
        locationState?.managerView?.department,
        locationState?.managerView?.departmentCode,
        locationState?.recordData?.department?.code,
        locationState?.recordData?.department?.name,
        user?.department_code,
    ]);
    const selectedPayperiodValue = useMemo(() => {
        if (locationState?.payPeriod?.trim()) {
            return locationState.payPeriod.trim();
        }

        const storageKey = locationState?.isManager ? "selectedManagerPayperiod" : "selectedPayperiod";
        return localStorage.getItem(storageKey) || "";
    }, [locationState?.isManager, locationState?.payPeriod]);
    const payPeriodLabel = useMemo(() => {
        if (!selectedPayperiodValue) {
            return "";
        }

        const matchedPayperiod = payperiodFilters.find(
            (payperiod) =>
                `${payperiod.start_datetime} - ${payperiod.end_datetime}` === selectedPayperiodValue
        );
        if (matchedPayperiod?.start_datetime && matchedPayperiod?.end_datetime) {
            return formatPayPeriodDisplay(matchedPayperiod.start_datetime, matchedPayperiod.end_datetime);
        }

        const [startDate = "", endDate = ""] = selectedPayperiodValue.split(" - ");
        if (!startDate || !endDate) {
            return "";
        }

        return formatPayPeriodDisplay(startDate, endDate);
    }, [payperiodFilters, selectedPayperiodValue]);
    const currentHoursLabel = useMemo(() => {
        const totalHoursString = groupedTimesheets?.data?.pay_period_totals?.total_hours_string;
        if (totalHoursString) {
            return totalHoursString;
        }

        const totalHours = groupedTimesheets?.data?.pay_period_totals?.total_hours;
        return typeof totalHours === "number" ? convertDecimalHoursToHM(totalHours) : "";
    }, [groupedTimesheets]);

    // Handlers for datetime inputs
    const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (shouldRestrictToTodayOnly && value && maxDateTimeInputValue && value > maxDateTimeInputValue) {
            toast.warn("Future dates are disabled for your profile.");
            return;
        }
        setStartDateTime(value);
    };
    const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (shouldRestrictToTodayOnly && value && maxDateTimeInputValue && value > maxDateTimeInputValue) {
            toast.warn("Future dates are disabled for your profile.");
            return;
        }
        setEndDateTime(value);
    };
    const isEditMode = locationState?.mode === "edit";
    const isManagerEntryFlow = Boolean(locationState?.isManager || locationState?.employeeId);
    const managerReturnPath =
        locationState?.managerReturnPath ||
        (locationState?.employeeId ? `/m-manager-timesheet/${locationState.employeeId}` : "/m-manager-timesheet");

    useEffect(() => {
        if (isManagerEntryFlow && !canEdit) {
            navigate(managerReturnPath, { replace: true });
        }
    }, [canEdit, isManagerEntryFlow, managerReturnPath, navigate]);

    // Initialize source path and back handler on mount
    useEffect(() => {
        console.log("location state :: ", locationState);
        console.log("managerReturnPath :: ", locationState?.managerReturnPath);
        // If we have location state with a specific path to return to, save it
        if (locationState?.managerReturnPath) {
            setSourcePath(locationState.managerReturnPath);
        } else if (locationState?.isManager) {
            // setSourcePath("/m-manager-timesheet");
            setSourcePath(`/m-manager-timesheet/${locationState?.employeeId}`);
        } else {
            setSourcePath("/m-timesheet");
        }

        // Set up global back handler - this will always reference the current version of handleBackNavigation
        setGlobalBackHandler(() => {
            handleBackNavigation();
            return true;
        });

        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [locationState, setSourcePath]);

    // Toggle Hours Entry mode
    const handleToggleHoursEntry = () => {
        setIsHoursEntry((prev) => {
            const newValue = !prev;
            setStartDateTime("");
            setEndDateTime("");
            setDuration(0);
            setHoursEntries(newValue ? [{ date: "", hours: 0 }] : []);
            setSite("");
            setJobCode("");
            setNote("");
            setBreaks([]);
            setLocalSelectedMap({}); // Reset selections
            setFieldStack([]);
            return newValue;
        });
    };

    // Open bottom sheet for a specific field
    const openTagSelector = (field: AttendanceTags) => {
        // Store this field as the root of the stack
        setFieldStack([field]);

        // Initialize the field's selection if it doesn't exist
        setLocalSelectedMap((prev) => ({
            ...prev,
            [field.field_code]: prev[field.field_code] || [],
        }));

        // Recursively ensure that all possible child fields are properly initialized
        const ensureChildFieldsInitialized = (parentField: AttendanceTags) => {
            const childFields = eligibleAttendanceTags.filter(
                (tag) => tag.parent_labor_dimension_id === String(parentField.labor_dimensions_id)
            );

            if (childFields.length > 0) {
                setLocalSelectedMap((prev) => {
                    const updated = { ...prev };
                    childFields.forEach((child) => {
                        if (!updated[child.field_code]) {
                            updated[child.field_code] = [];
                        }
                    });
                    return updated;
                });

                // Recursively ensure their children are initialized too
                childFields.forEach((child) => ensureChildFieldsInitialized(child));
            }
        };

        ensureChildFieldsInitialized(field);

        // In edit mode, if we have selections for this field, we need to set up the field stack properly
        const isEditMode = locationState?.mode === "edit";
        if (isEditMode && locationState?.recordData) {
            // Use recordData directly to verify tag selections
            const recordData = locationState.recordData;
            if (recordData.tags && Array.isArray(recordData.tags)) {
                // Find tags that match our current field
                const fieldTag = recordData.tags.find((tag: any) => tag.field_code === field.field_code);

                if (fieldTag && fieldTag.field_values && fieldTag.field_values.length > 0) {
                    // We have confirmed selections from recordData, now find child selections
                    const buildFieldStack = (currentField: AttendanceTags): AttendanceTags[] => {
                        const stack = [currentField];

                        // Look for child fields that have selections based on recordData
                        const childFields = eligibleAttendanceTags.filter(
                            (tag) => tag.parent_labor_dimension_id === String(currentField.labor_dimensions_id)
                        );

                        for (const childField of childFields) {
                            // Check if this child has selections in recordData
                            const childFieldTag = recordData.tags.find(
                                (tag: any) => tag.field_code === childField.field_code
                            );

                            if (childFieldTag && childFieldTag.field_values && childFieldTag.field_values.length > 0) {
                                // This child has selections, add it and its descendants to the stack
                                const childStack = buildFieldStack(childField);
                                return [...stack, ...childStack];
                            }
                        }

                        return stack;
                    };

                    // Update field stack with the complete path of selected fields from recordData
                    const fullStack = buildFieldStack(field);
                    if (fullStack.length > 1) {
                        // Only update if we found children
                        setFieldStack(fullStack);
                    }
                }
            }
        }

        setActiveTagField(field);
        setShowBottomSheet(true);
    };

    // Handler called by AttendanceTagSelector on selection changes for current root field
    const handleTagOptionSelect = (
        rootFieldCode: string,
        selected: { value_code: string; value_text: string }[],
        allSelections?: {
            field_code: string;
            field_values: { value_code: string; value_text: string }[];
        }[]
    ) => {
        // If we received all selections with the new format, use that
        if (allSelections && allSelections.length > 0) {
            // Reset the map and add all the new selections
            const newMap: Record<string, { value_code: string; value_text: string }[]> = {};

            // Add each field's selections to the map
            allSelections.forEach((item) => {
                newMap[item.field_code] = item.field_values;
            });

            // Replace the entire selection map
            setLocalSelectedMap(newMap);
        } else {
            // Fall back to the old behavior if allSelections wasn't provided
            setLocalSelectedMap((prev) => ({
                ...prev,
                [rootFieldCode]: selected,
            }));
        }
    };

    function buildTagsPayload(
        localSelectedMap: Record<string, { value_code: string; value_text: string }[]>,
        attendanceTags: AttendanceTags[]
    ): SelectedTags[] {
        // First identify root tags (those without parents or with null parent)
        const rootFields = attendanceTags.filter(
            (tag) => tag.parent_labor_dimension_id === null || tag.parent_labor_dimension_id === ""
        );

        const rootFieldCodes = rootFields.map((field) => field.field_code);

        // Build a map of fields by their IDs for quick lookup
        const fieldsById = attendanceTags.reduce((acc, tag) => {
            acc[String(tag.labor_dimensions_id)] = tag;
            return acc;
        }, {} as Record<string, AttendanceTags>);

        // Build a map of parent-child relationships
        const childrenByParentId: Record<string, AttendanceTags[]> = {};
        attendanceTags.forEach((tag) => {
            if (tag.parent_labor_dimension_id) {
                if (!childrenByParentId[tag.parent_labor_dimension_id]) {
                    childrenByParentId[tag.parent_labor_dimension_id] = [];
                }
                childrenByParentId[tag.parent_labor_dimension_id].push(tag);
            }
        });

        // Filter out any local selections that aren't in our map or don't have values
        const validSelections = Object.entries(localSelectedMap).filter(([_, values]) => values && values.length > 0);

        // Split into root selections and child selections
        const rootSelections = validSelections.filter(([field_code]) => rootFieldCodes.includes(field_code));

        // Start with root tags
        const result: SelectedTags[] = rootSelections.map(([field_code, values]) => ({
            field_code,
            field_values: values.map((v) => ({
                value_code: v.value_code,
                value_text: v.value_text,
            })),
        }));

        console.log("result :: ", result);

        // If no root selections, return empty array
        if (result.length === 0) {
            return [];
        }

        // Store all non-root selections, organized by their level in the hierarchy
        const findFieldLevelAndPath = (fieldCode: string): { level: number; path: string[] } => {
            // Find the field by its code
            const field = attendanceTags.find((t) => t.field_code === fieldCode);
            if (!field) return { level: 0, path: [] };

            // If it's a root field, return level 1
            if (!field.parent_labor_dimension_id) {
                return { level: 1, path: [field.field_code] };
            }

            // Otherwise, find its parent and add 1 to the parent's level
            let current = field;
            const path = [fieldCode];
            let level = 1;

            // Traverse up the hierarchy until we reach a root field
            while (current.parent_labor_dimension_id) {
                const parentField = attendanceTags.find(
                    (t) => String(t.labor_dimensions_id) === current.parent_labor_dimension_id
                );

                if (!parentField) break;

                path.unshift(parentField.field_code);
                current = parentField;
                level++;
            }

            return { level, path };
        };

        // Group non-root selections by their hierarchy level and path
        const nonRootSelections = validSelections
            .filter(([field_code]) => !rootFieldCodes.includes(field_code))
            .map(([field_code, values]) => {
                const { level, path } = findFieldLevelAndPath(field_code);
                return {
                    field_code,
                    values,
                    level,
                    path,
                };
            })
            // Sort by level to process in order from higher levels to lower levels
            .sort((a, b) => a.level - b.level);

        // Add non-root selections as separate items in the result array
        // instead of nesting them under their parent tags
        if (nonRootSelections.length > 0) {
            nonRootSelections.forEach(({ field_code, values }) => {
                // Find the tag info for this field
                const tagInfo = attendanceTags.find((t) => t.field_code === field_code);

                if (tagInfo) {
                    // Add the child selection as a separate entry in the result
                    result.push({
                        field_code,
                        field_values: values.map((value) => ({
                            value_code: value.value_code,
                            value_text: value.value_text,
                        })),
                    });
                }
            });
        }

        return result;
    }

    // Enhanced form validation
    const validateForm = (): boolean => {
        /**
         * removing validation for site, department and job code
         * @vishwesh5544
         */
        // Require site, department and job code selections
        // if (!site) {
        //     toast.error("Please select a site");
        //     return false;
        // } else if (!department) {
        //     toast.error("Please select a department");
        //     return false;
        // } else if (!jobCode) {
        //     toast.error("Please select a job code");
        //     return false;
        // }

        // Validate Hours entries if in hours mode
        if (isHoursEntry) {
            // Check if entries exist
            if (!hoursEntries.length) {
                toast.error("Please add at least one time entry");
                return false;
            }

            // More comprehensive validation for each entry
            const today = new Date(todayReferenceDate);
            for (let i = 0; i < hoursEntries.length; i++) {
                const entry = hoursEntries[i];

                // Check for empty date
                if (!entry.date) {
                    toast.error(`Please enter a date for entry ${i + 1}`);
                    return false;
                }

                // Check for valid date range (not too far in the past or future)
                const entryDate = new Date(entry.date);
                const minDate = new Date();
                minDate.setFullYear(today.getFullYear() - 1); // Allow dates up to 1 year in the past

                if (entryDate < minDate) {
                    toast.error(`Date for entry ${i + 1} is too far in the past`);
                    return false;
                }

                if (shouldRestrictToTodayOnly) {
                    if (entryDate > endOfTodayDate) {
                        toast.error(`Future dates are disabled (entry ${i + 1})`);
                        return false;
                    }
                }

                // Check for valid hours
                if (entry.hours <= 0) {
                    toast.error(`Please enter valid hours for entry ${i + 1}`);
                    return false;
                }

                if (entry.hours > 24) {
                    toast.error(`Hours for entry ${i + 1} cannot exceed 24 hours`);
                    return false;
                }
            }

            // Check for duplicate dates
            const dateSet = new Set<string>();
            for (const entry of hoursEntries) {
                if (dateSet.has(entry.date)) {
                    toast.error("Duplicate dates found. Please use a single entry per date");
                    return false;
                }
                dateSet.add(entry.date);
            }
        } else {
            // Validate date range for duration entry
            if (!startDateTime) {
                toast.error("Please enter valid start datetime");
                return false;
            }

            /**
             * removing validation for endDateTime
             * @vishwesh5544
             */
            // if (!endDateTime) {
            //     toast.error("Please enter valid end datetime");
            //     return false;
            // }

            // Calculate duration in hours
            const startDate = new Date(startDateTime);
            const endDate = new Date(endDateTime);

            if (endDate <= startDate) {
                toast.error("End time must be after start time");
                return false;
            }

            const durationMs = endDate.getTime() - startDate.getTime();
            const durationHours = durationMs / (1000 * 60 * 60);

            if (durationHours > 24) {
                toast.error("Duration cannot exceed 24 hours");
                return false;
            }
        }

        // Validate required tags
        for (const field of visibleRootAttendanceTags) {
            const isRequired =
                field?.required === "true" ||
                field?.required === true ||
                field?.other_info_json?.required === true ||
                (field?.other_info_json?.required as any) === "true";
            if (isRequired) {
                const selected = localSelectedMap[field.field_code];
                if (!selected || selected.length === 0) {
                    toast.error(`Please select a value for "${field.field_name}"`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const tagsPayload = buildTagsPayload(localSelectedMap, eligibleAttendanceTags);
        const breaksPayload = filterBreaksByType(breaks); // Filter breaks by type before sending
        const isEditMode = locationState?.mode === "edit";
        // Send the device's current local time in MySQL format so backend can anchor
        // rolling timesheet-window checks to the same client-visible "now" when needed.
        const currentDateTime = DateTimeUtil.formatDateToMySQL(new Date());

        // Set submitting state to display loading spinner
        setSubmitting(true);

        if (isEditMode && locationState?.recordId) {
            // For edit mode, use adjustTimeCardApi
            const adjustPayload: AdjustTimeCardPayload = isHoursEntry
                ? {
                      record_id: locationState.recordId,
                      employee_id: locationState?.employeeId,
                      current_datetime: currentDateTime,
                      entry_type: "HOURS",
                      hours_entries: hoursEntries.map((entry) => ({
                          ...entry,
                          date: entry.date ? DateTimeUtil.formatDateToMySQL(new Date(entry.date), true) : entry.date,
                          breaks: filterBreaksByType(breaks).map((breakItem) => {
                              // Format datetime fields in breaks if present
                              if (breakItem.start_datetime && breakItem.end_datetime) {
                                  return {
                                      ...breakItem,
                                      start_datetime: DateTimeUtil.formatDateToMySQL(
                                          new Date(breakItem.start_datetime)
                                      ),
                                      end_datetime: DateTimeUtil.formatDateToMySQL(new Date(breakItem.end_datetime)),
                                  };
                              }
                              return breakItem;
                          }),
                      })),
                      department_code: department,
                      site_code: site,
                      work_code: jobCode,
                      notes: note,
                      tags: tagsPayload?.length ? tagsPayload : null,
                  }
                : {
                      record_id: locationState.recordId,
                      employee_id: locationState?.employeeId,
                      current_datetime: currentDateTime,
                      entry_type: "DURATION",
                      duration_entry: {
                          start_datetime: startDateTime ? DateTimeUtil.formatDateToMySQL(new Date(startDateTime)) : "",
                          end_datetime: endDateTime ? DateTimeUtil.formatDateToMySQL(new Date(endDateTime)) : "",
                          breaks: filterBreaksByType(breaks).map((breakItem) => {
                              // Format datetime fields in breaks if present
                              if (breakItem.start_datetime && breakItem.end_datetime) {
                                  return {
                                      ...breakItem,
                                      start_datetime: DateTimeUtil.formatDateToMySQL(
                                          new Date(breakItem.start_datetime)
                                      ),
                                      end_datetime: DateTimeUtil.formatDateToMySQL(new Date(breakItem.end_datetime)),
                                  };
                              }
                              return breakItem;
                          }),
                      },
                      department_code: department,
                      site_code: site,
                      work_code: jobCode,
                      notes: note,
                      // Keep the same tag structure as when adding
                      tags: tagsPayload?.length ? tagsPayload : null,
                  };

            dispatch(adjustTimeCardApi(adjustPayload))
                .unwrap()
                .then((res) => {
                    toast.success("Timecard updated successfully!");
                    // Use managerReturnPath if available
                    if (locationState?.managerReturnPath) {
                        navigate(locationState.managerReturnPath, { replace: true });
                    } else if (locationState?.isManager) {
                        // Navigate back to manager timesheet view for the specific employee
                        navigate(`/m-manager-timesheet/${locationState?.employeeId}`, { replace: true });
                    } else {
                        // Use replace: true to remove AddTimesheetEntry from history stack
                        navigate(`/m-timecard-record-detail/${locationState.recordId}`, { replace: true });
                    }
                })
                .catch((err) => {
                    console.error("Error updating timecard:", err);
                    toast.error(typeof err === "string" ? err : err?.message || "Failed to update timecard. Please try again.");
                })
                .finally(() => setSubmitting(false));
        } else {
            // For add mode, use addTimecardRecord
            const payload: AddTimecardPayload = isHoursEntry
                ? {
                      employee_id: locationState?.employeeId,
                      current_datetime: currentDateTime,
                      entry_type: "HOURS",
                      site_code: site,
                      work_code: jobCode,
                      department_code: department,
                      tags: tagsPayload.length ? tagsPayload : null,
                      notes: note,
                      hours_entries: hoursEntries.map((entry) => ({
                          ...entry,
                          date: entry.date,
                          breaks: breaksPayload.map((breakItem) => {
                              // Format datetime fields in breaks if present
                              if (breakItem.start_datetime && breakItem.end_datetime) {
                                  return {
                                      ...breakItem,
                                      start_datetime: DateTimeUtil.formatDateToMySQL(
                                          new Date(breakItem.start_datetime)
                                      ),
                                      end_datetime: DateTimeUtil.formatDateToMySQL(new Date(breakItem.end_datetime)),
                                  };
                              }
                              return breakItem;
                          }),
                      })),
                  }
                : {
                      employee_id: locationState?.employeeId,
                      current_datetime: currentDateTime,
                      entry_type: "DURATION",
                      site_code: site,
                      work_code: jobCode,
                      department_code: department,
                      tags: tagsPayload.length ? tagsPayload : null,
                      notes: note,
                      duration_entry: {
                          start_datetime: startDateTime ? DateTimeUtil.formatDateToMySQL(new Date(startDateTime)) : "",
                          end_datetime: endDateTime ? DateTimeUtil.formatDateToMySQL(new Date(endDateTime)) : "",
                          breaks: breaksPayload.map((breakItem) => {
                              // Format datetime fields in breaks if present
                              if (breakItem.start_datetime && breakItem.end_datetime) {
                                  return {
                                      ...breakItem,
                                      start_datetime: DateTimeUtil.formatDateToMySQL(
                                          new Date(breakItem.start_datetime)
                                      ),
                                      end_datetime: DateTimeUtil.formatDateToMySQL(new Date(breakItem.end_datetime)),
                                  };
                              }
                              return breakItem;
                          }),
                      },
                  };

            // console.log("tagsPayload", tagsPayload);
            // console.log("breaksPayload", breaksPayload);
            // console.log("payload", payload);
            dispatch(addTimecardRecord(payload))
                .unwrap()
                .then((res) => {
                    toast.success(res.message || "Timecard entry added successfully!");
                    // Use managerReturnPath if available
                    if (locationState?.managerReturnPath) {
                        navigate(locationState.managerReturnPath);
                    } else if (locationState?.isManager) {
                        // Navigate back to manager timesheet view for the specific employee
                        navigate(`/m-manager-timesheet/${locationState?.employeeId}`);
                    } else {
                        navigate("/m-timesheet");
                    }
                })
                .catch((err) => {
                    console.error("Error adding timecard:", err);
                    toast.error(typeof err === "string" ? err : err?.message || "Failed to add timecard. Please try again.");
                })
                .finally(() => setSubmitting(false));
        }
    };

    // Handle back button press
    const handleBackNavigation = () => {
        navigate(-1);
        // // Try to navigate using the navigation history
        // const sourcePath = getSourcePath();

        // if (sourcePath) {
        //     // We have a recorded source path, use it
        //     navigate(sourcePath);
        // }
        // // Fallbacks if source path isn't available
        // else if (locationState?.managerReturnPath) {
        //     navigate(locationState.managerReturnPath);
        // } else if (locationState?.isManager) {
        //     navigate(`/m-manager-timesheet/${locationState?.employeeId}`);
        // } else {
        //     navigate("/m-timesheet");
        // }
    };

    useEffect(() => {
        dispatch(setShowTopNav(true));
        dispatch(setTopNavNameTitle(isEditMode ? "Edit TimeCard Entry" : "Add TimeCard Entry"));

        // Set up back button behavior in the header
        const handleBackButton = () => {
            handleBackNavigation();
            return true; // Prevent default back behavior
        };

        // Add event listener for hardware back button (mobile devices)
        window.addEventListener("popstate", handleBackButton);

        // Clean up event listener when component unmounts
        return () => {
            window.removeEventListener("popstate", handleBackButton);
        };
    }, [navigate]);

    // We've replaced navigation to BreakManagerPage with an overlay component
    // So we don't need to handle returning from that page anymore

    useEffect(() => {
        setLoading(true);
        Promise.all([
            // dispatch(getSites()),
            dispatch(getDepartments()),
            dispatch(getJobCodes()),
            // dispatch(fetchAttendanceTags()),
            dispatch(
                getSitesV2({
                    employee_id: locationState?.employeeId ?? "",
                })
            ),
            dispatch(
                fetchAttendanceTagsV2({
                    employee_id: locationState?.employeeId ?? "",
                    tag_type: 1,
                })
            ),
        ]).then(() => {
            // If in edit mode, populate the form with existing data
            const isEditMode = locationState?.mode === "edit";
            if (locationState?.managerView?.departmentCode) {
                setDepartment(locationState.managerView.departmentCode);
            }

            if (isEditMode && locationState?.recordData) {
                const recordData = locationState.recordData;

                console.log("recordData", recordData);
                // Set start and end date/time
                if (recordData.os_emp_start_datetime) {
                    setStartDateTime(convertToDateTimeLocalFormat(recordData.os_emp_start_datetime));
                }

                if (recordData.os_emp_end_datetime) {
                    setEndDateTime(convertToDateTimeLocalFormat(recordData.os_emp_end_datetime));
                }

                // Set department
                if (recordData.department?.code) {
                    setDepartment(recordData.department.code);
                }

                // Set site
                if (recordData.site?.code) {
                    setSite(recordData.site.code);
                }

                // Set job code
                if (recordData.work_code?.code) {
                    setJobCode(recordData.work_code.code);
                }

                // Set note if available
                // if (recordData.notes) {
                //     setNote(recordData.notes);
                // }

                // Load breaks if available
                if (recordData.breaktimes_json && recordData.breaktimes_json.breaktimes) {
                    console.log("Loading breaks data from recordData:", recordData.breaktimes_json.breaktimes);
                    try {
                        // Convert from Breaktime format to BreakDuration format
                        const loadedBreaks: BreakDuration[] = recordData.breaktimes_json.breaktimes.map(
                            (breakItem: any) => {
                                // Determine if this is a time-based or duration-based break
                                if (breakItem.start_datetime && breakItem.end_datetime) {
                                    // This is a time-based break
                                    return {
                                        type: breakItem.is_paid ? "paid" : "unpaid",
                                        start_datetime: breakItem.start_datetime,
                                        end_datetime: breakItem.end_datetime,
                                    };
                                } else if (breakItem.duration_in_minutes !== undefined) {
                                    // This is a duration-based break
                                    return {
                                        type: breakItem.is_paid ? "paid" : "unpaid",
                                        duration_minutes: breakItem.duration_in_minutes,
                                    };
                                } else {
                                    // Neither time-based nor duration-based, create with default duration
                                    return {
                                        type: breakItem.is_paid ? "paid" : "unpaid",
                                        duration_minutes: 30, // Default duration
                                    };
                                }
                            }
                        );
                        console.log("Loaded breaks data:", loadedBreaks);
                        setBreaks(loadedBreaks);
                    } catch (error) {
                        console.error("Error loading breaks data:", error);
                    }
                }

                // Set tags if available - this is important to properly initialize the tag selections
                if (recordData.attendence_tags && Array.isArray(recordData.attendence_tags)) {
                    const newSelectedMap: Record<string, { value_code: string; value_text: string }[]> = {};

                    // Process each tag and add to the selection map
                    recordData.attendence_tags.forEach(
                        (tag: {
                            field_code: string;
                            field_values: Array<{ value_code: string; value_text: string }>;
                        }) => {
                            if (tag.field_code && Array.isArray(tag.field_values)) {
                                newSelectedMap[tag.field_code] = tag.field_values.map(
                                    (value: { value_code: string; value_text: string }) => ({
                                        value_code: value.value_code,
                                        value_text: value.value_text,
                                    })
                                );
                            }
                        }
                    );

                    // Update the local selected map with the existing values
                    setLocalSelectedMap(newSelectedMap);
                }
            }
            setLoading(false);
        });
    }, [dispatch, locationState, convertToDateTimeLocalFormat]);

    // Enhanced breadcrumb builder that handles all tag scenarios
    function getSelectedBreadcrumb(
        field: AttendanceTags,
        selectedMap: Record<string, { value_code: string; value_text: string }[]>,
        allFields: AttendanceTags[]
    ): string {
        const selectedValues = selectedMap[field.field_code];
        if (!selectedValues || selectedValues.length === 0) return "";

        // Build a unified breadcrumb path that handles both single and multi-select
        const buildBreadcrumb = (
            startField: AttendanceTags,
            selMap: Record<string, { value_code: string; value_text: string }[]>
        ): string => {
            const selected = selMap[startField.field_code];
            if (!selected || selected.length === 0) return "";

            // Handle multiple selections with comma separation
            let value: string;
            if (startField.multiselect === "true" && selected.length > 1) {
                value = selected.map((v) => v.value_text).join(", ");
            } else {
                value = selected[0].value_text;
            }

            // Check for child selections - scan all possible child fields
            const childFields = allFields.filter(
                (f) => f.parent_labor_dimension_id === String(startField.labor_dimensions_id)
            );

            // If we have child fields with selections, build their breadcrumbs
            if (childFields.length > 0) {
                for (const childField of childFields) {
                    const childSelected = selMap[childField.field_code];
                    if (childSelected && childSelected.length > 0) {
                        const childBreadcrumb = buildBreadcrumb(childField, selMap);
                        if (childBreadcrumb) {
                            return `${value} > ${childBreadcrumb}`;
                        }
                    }
                }
            }

            return value;
        };

        // Create a full breadcrumb for this field hierarchy
        const breadcrumb = buildBreadcrumb(field, selectedMap);

        // For edit mode, ensure we build full paths that match the JSON from the API
        const isEditMode = locationState?.mode === "edit";
        if (isEditMode) {
            const getFullPath = (field: AttendanceTags): AttendanceTags[] => {
                const path = [field];

                // Loop through the hierarchy, finding child fields that have selections
                let currentField = field;
                let foundChild = true;

                while (foundChild) {
                    foundChild = false;
                    const childFields = allFields.filter(
                        (f) => f.parent_labor_dimension_id === String(currentField.labor_dimensions_id)
                    );

                    for (const childField of childFields) {
                        const childSelected = selectedMap[childField.field_code];
                        if (childSelected && childSelected.length > 0) {
                            path.push(childField);
                            currentField = childField;
                            foundChild = true;
                            break; // We've found a child with selections, move to the next level
                        }
                    }
                }

                return path;
            };

            // If there's more than just the root field, ensure the full path is reflected in the breadcrumb
            const fullPath = getFullPath(field);
            if (fullPath.length > 1) {
                return breadcrumb; // The buildBreadcrumb function already handles this correctly
            }
        }

        return breadcrumb;
    }

    const getNoteLabel = (): string => {
        const defaultLabel = "Enter note";
        // if(isEditMode && locationState?.recordData) {
        //     const record = locationState.recordData;

        //     const notesArr = record.notes;
        //     const label = notesArr.map((n: any) => n.notes_json?.notes);
        //     return label;
        // }

        return defaultLabel;
    };

    return (
        <div className="max-w-full mx-4 space-y-4 mt-4 overflow-y-auto h-[calc(100vh-80px)] mx-auto md:w-1/2 px-4">
            {loading ? (
                <div className="flex justify-center items-center py-4">
                    <Loader size={32} color="#2664eb" strokeWidth={2.5} className="animate-spin" />
                </div>
            ) : (
                <>
                    <div className="mb-6 hidden items-center justify-between md:flex">
                        <button
                            onClick={handleBackNavigation}
                            className="flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-800"
                        >
                            <ArrowLeft size={20} />
                            <span>Back</span>
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800">
                            {isEditMode ? "Edit Timecard Entry" : "Add Timecard Entry"}
                        </h1>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                            <NgwAvatar src={employeeImageUrl} name={employeeDisplayName} className="w-12 h-12" />
                            <div className="min-w-0 flex-1">
                                <div className="mt-1 truncate text-lg font-semibold text-gray-900">
                                    {employeeDisplayName}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
                                    {departmentDisplay && <span>{departmentDisplay}</span>}
                                </div>
                            </div>
                        </div>

                        {(payPeriodLabel || currentHoursLabel) && (
                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {payPeriodLabel && (
                                    <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                            Pay Period
                                        </div>
                                        <div className="mt-1 text-base font-semibold leading-tight text-gray-900">
                                            {payPeriodLabel}
                                        </div>
                                    </div>
                                )}
                                {currentHoursLabel && (
                                    <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                            Current Hours
                                        </div>
                                        <div className="mt-1 text-base font-semibold leading-tight text-gray-900">
                                            {currentHoursLabel}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* <div className="flex items-center mb-2">
            <button 
              onClick={handleBackNavigation}
              className="flex items-center text-blue-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Timesheet
            </button>
          </div> */}

                    <div className="flex justify-between items-center">
                        <label className="cursor-pointer font-medium">{isHoursEntry ? "Hours " : "Duration"}</label>
                        {locationState?.mode !== "edit" && (
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isHoursEntry}
                                onClick={handleToggleHoursEntry}
                                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                                    isHoursEntry ? "bg-blue-600" : "bg-gray-300"
                                }`}
                            >
                                <span
                                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                                        isHoursEntry ? "translate-x-6" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        )}
                    </div>

                    {!isHoursEntry ? (
                        <>
                            <label className="block text-md font-medium">Start Date & Time</label>
                            <input
                                required
                                type="datetime-local"
                                value={startDateTime}
                                onChange={handleStartDateChange}
                                max={shouldRestrictToTodayOnly ? maxDateTimeInputValue : undefined}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                                placeholder="Start Date & Time"
                            />

                            <label className="block text-md font-medium mt-4">End Date & Time</label>
                            <input
                                type="datetime-local"
                                value={endDateTime}
                                onChange={handleEndDateChange}
                                min={startDateTime}
                                max={shouldRestrictToTodayOnly ? maxDateTimeInputValue : undefined}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                                placeholder="End Date & Time"
                            />

                            <div className="flex justify-between mt-4">
                                <label className="block text-md font-medium">Duration</label>
                                <span>{formatDurationStartEndDate(startDateTime, endDateTime) || "0"}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            {hoursEntries.map((entry, index) => (
                                <div key={index} className="flex items-center space-x-2 mb-2">
                                    <div className="flex-1">
                                        <label className="block text-md font-medium">Date</label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={entry.date}
                                                onChange={(e) => {
                                                    const selectedDate = e.target.value;
                                                    const newEntries = [...hoursEntries];

                                                    // Apply some basic validation
                                                    if (selectedDate) {
                                                        const date = new Date(selectedDate);
                                                        const today = new Date();
                                                        const oneYearAgo = new Date();
                                                        oneYearAgo.setFullYear(today.getFullYear() - 1);

                                                        if (
                                                            shouldRestrictToTodayOnly &&
                                                            maxDateInputValue &&
                                                            selectedDate > maxDateInputValue
                                                        ) {
                                                            toast.warn("Future dates are disabled for your profile.");
                                                            return;
                                                        }

                                                        if (date < oneYearAgo) {
                                                            toast.warn("Date cannot be more than 1 year in the past");
                                                            return;
                                                        }
                                                    }

                                                    newEntries[index].date = selectedDate;
                                                    setHoursEntries(newEntries);

                                                    // Check for duplicate dates
                                                    const dateCount = hoursEntries.filter(
                                                        (e) => e.date === selectedDate
                                                    ).length;
                                                    if (dateCount > 1) {
                                                        toast.warn(
                                                            "This date already has an entry. Please use a different date."
                                                        );
                                                    }
                                                }}
                                                className="w-full mt-1 p-2 border-2 border-b border-gray-300 rounded-md text-gray-500"
                                                placeholder="Date"
                                                required
                                                max={shouldRestrictToTodayOnly ? maxDateInputValue : undefined}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-0">
                                        <label className="block text-md font-medium">Hours</label>
                                        <div className="relative flex items-center gap-1 mt-1">
                                            <input
                                                type="number"
                                                min="0"
                                                max="23"
                                                value={entry.hours > 0 ? Math.floor(entry.hours) : ""}
                                                onChange={(e) => {
                                                    const hours = parseInt(e.target.value) || 0;
                                                    const minutes = Math.round((entry.hours - Math.floor(entry.hours)) * 60);
                                                    const newEntries = [...hoursEntries];
                                                    newEntries[index].hours = hours + minutes / 60;
                                                    setHoursEntries(newEntries);
                                                }}
                                                placeholder="00"
                                                className="p-2 border-2 border-b border-gray-300 rounded-md text-gray-700 text-center"
                                                required
                                            />
                                            <span className="text-gray-700 font-bold">:</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="59"
                                                value={entry.hours > 0 ? Math.round((entry.hours - Math.floor(entry.hours)) * 60) : ""}
                                                onChange={(e) => {
                                                    const minutes = parseInt(e.target.value) || 0;
                                                    const hours = Math.floor(entry.hours);
                                                    const newEntries = [...hoursEntries];
                                                    newEntries[index].hours = hours + minutes / 60;
                                                    setHoursEntries(newEntries);
                                                }}
                                                placeholder="00"
                                                className="p-2 border-2 border-b border-gray-300 rounded-md text-gray-700 text-center"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-red-500 font-bold mt-6"
                                        onClick={() => {
                                            // Remove this entry row
                                            const newEntries = hoursEntries.filter((_, i) => i !== index);
                                            setHoursEntries(newEntries.length ? newEntries : [{ date: "", hours: 0 }]);
                                        }}
                                        aria-label="Remove entry"
                                    >
                                        {/* &times; */}
                                        <CircleX />
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                className={`px-4 py-1  border-solid border-2  border-blue-600 hover:border-white-600 rounded-md bg-blue-600 font-extrabold  ${
                                    canAddMore
                                        ? "text-blue-600 hover:text-blue-700 hover:bg-gray-200 hover:text-white-200"
                                        : "text-gray-400 cursor-not-allowed"
                                }`}
                                onClick={() => {
                                    if (!canAddMore) {
                                        // Show validation message if entries are incomplete
                                        const emptyDateEntries = hoursEntries.filter((e) => !e.date.trim());
                                        const zeroHourEntries = hoursEntries.filter((e) => e.hours <= 0);

                                        if (emptyDateEntries.length > 0) {
                                            toast.error("Please enter a date for all entries");
                                        } else if (zeroHourEntries.length > 0) {
                                            toast.error("Please enter hours for all entries");
                                        }
                                        return;
                                    }
                                    setHoursEntries([...hoursEntries, { date: "", hours: 0 }]);
                                }}
                                aria-label="Add another time entry"
                                title="Add another time entry"
                            >
                                <Plus size={20} color="white" strokeWidth={3} />
                            </button>
                        </>
                    )}

                    {/* Break Manager for Duration Entry - Always visible */}
                    {!isHoursEntry && (
                        <div className="rounded-lg">
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-gray-800 text-md font-medium">Breaks</label>
                                {/* <span className="text-md font-bold">{breaks.length > 0 ? "3hr 0min" : ""}</span> */}
                            </div>

                            {/* Break Summary */}
                            {breaks.length > 0 ? (
                                <div className="mt-2">
                                    {breaks.map((breakItem, idx) => (
                                        <div key={idx} className="flex justify-between items-center mb-3">
                                            <div>
                                                <div className="text-gray-800">
                                                    {breakItem.start_datetime && breakItem.end_datetime ? (
                                                        <>
                                                            {new Date(breakItem.start_datetime).toLocaleString(
                                                                "en-US",
                                                                {
                                                                    hour: "numeric",
                                                                    minute: "2-digit",
                                                                    hour12: true,
                                                                }
                                                            )}{" "}
                                                            -{" "}
                                                            {new Date(breakItem.end_datetime).toLocaleString("en-US", {
                                                                hour: "numeric",
                                                                minute: "2-digit",
                                                                hour12: true,
                                                            })}
                                                        </>
                                                    ) : (
                                                        "Duration-based break"
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {breakItem.duration_minutes !== undefined
                                                        ? `${breakItem.duration_minutes} min`
                                                        : breakItem.start_datetime && breakItem.end_datetime
                                                        ? `${calculateDurationMinutes(
                                                              breakItem.start_datetime,
                                                              breakItem.end_datetime
                                                          )} min`
                                                        : "0 min"}{" "}
                                                    • {breakItem.type === "paid" ? "Paid" : "Unpaid"}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        // Open break manager overlay in edit mode
                                                        setEditBreakIndex(idx);
                                                        setShowBreakManager(true);
                                                    }}
                                                    className="text-blue-600"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const updatedBreaks = [...breaks];
                                                        updatedBreaks.splice(idx, 1);
                                                        setBreaks(updatedBreaks);
                                                    }}
                                                    className="text-red-600"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            <button
                                type="button"
                                onClick={() => {
                                    setEditBreakIndex(null);
                                    setShowBreakManager(true);
                                }}
                                className="text-blue-600 mt-3 flex items-center"
                            >
                                + Add Break
                            </button>
                        </div>
                    )}

                    {attendanceOption?.code && jobCodeSelectOptions.length > 0 && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Code</label>
                            <Select
                                value={jobCodeSelectOptions.find((option) => option.value === jobCode) || null}
                                onChange={(selectedOption: any) => setJobCode(selectedOption?.value)}
                                placeholder="Select Job Code"
                                options={jobCodeSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {attendanceOption?.site && siteSelectOptions.length > 0 && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Site</label>
                            <Select
                                value={siteSelectOptions.find((option) => option.value === site) || null}
                                onChange={(selectedOption: any) => setSite(selectedOption?.value)}
                                placeholder="Select Site"
                                options={siteSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {attendanceOption?.department && departmentSelectOptions.length > 0 && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Department</label>
                            <Select
                                value={departmentSelectOptions.find((option) => option.value === department) || null}
                                onChange={(selectedOption: any) => setDepartment(selectedOption?.value)}
                                placeholder="Select Department"
                                options={departmentSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {/* Render attendance tag buttons */}
                    {attendanceOption?.tags && (
                        <>
                            {/* First, render any saved tags that don't exist in current config */}
                            {Object.keys(localSelectedMap).map((savedFieldCode) => {
                                // Check if this saved field exists in the current config
                                const existsInCurrentConfig = rootAttendanceTags.some(
                                    f => f.field_code === savedFieldCode
                                );
                                
                                // Only render if it DOESN'T exist in current config (legacy data)
                                if (!existsInCurrentConfig && localSelectedMap[savedFieldCode]?.length > 0) {
                                    const savedValues = localSelectedMap[savedFieldCode];
                                    const displayValue = savedValues.map(v => v.value_text).join(", ");
                                    
                                    return (
                                        <div key={`legacy-${savedFieldCode}`} className="mt-4">
                                            <label className="block font-semibold mb-1">
                                                {savedFieldCode}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Create a temporary field object for saved legacy data
                                                    const tempField: AttendanceTags = {
                                                        labor_dimensions_id: 0,
                                                        user_account_id: '',
                                                        field_code: savedFieldCode,
                                                        field_name: savedFieldCode,
                                                        field_type: 'Dropdown',
                                                        field_values: savedValues.map(v => v.value_code),
                                                        field_json: savedValues.map(v => ({
                                                            field_value_code: v.value_code,
                                                            field_value_text: v.value_text
                                                        })),
                                                        field_description: null,
                                                        created: '',
                                                        parent_labor_dimension_id: null,
                                                        multiselect: 'false',
                                                        required: 'false',
                                                        other_info_json: {
                                                            required: false,
                                                            multiselect: false,
                                                            requiredfor: null
                                                        },
                                                        tag_type: '',
                                                        default_values: []
                                                    };
                                                    openTagSelector(tempField);
                                                }}
                                                className="w-full p-3 text-left border border-gray-300 rounded-md text-gray-900"
                                            >
                                                {displayValue}
                                            </button>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                            
                            {/* Then render current config fields */}
                            {visibleRootAttendanceTags.map((field) => {
                            if (field.field_type === "Dropdown") {
                                const selectedBreadcrumb = getSelectedBreadcrumb(field, localSelectedMap, eligibleAttendanceTags);
                                const hasSelection = selectedBreadcrumb && selectedBreadcrumb.trim().length > 0;
                                
                                return (
                                    <div key={field.labor_dimensions_id} className="mt-4">
                                        <label className="block font-semibold mb-1">
                                            {field.field_name}
                                            {(field?.required === "true" || field?.required === true) && <span className="text-red-500">*</span>}
                                        </label>

                                        <button
                                            type="button"
                                            onClick={() => openTagSelector(field)}
                                            className={`w-full p-3 text-left border border-gray-300 rounded-md ${
                                                hasSelection ? 'text-gray-900' : 'text-gray-500'
                                            }`}
                                        >
                                            {hasSelection ? selectedBreadcrumb : `Select ${field.field_name}`}
                                        </button>
                                    </div>
                                );
                            }

                            // ... your existing Toggle and Text fields logic here ...
                            //             // For Toggle and Text fields, you can keep your existing inputs or convert similarly
                            if (field.field_type === "Toggle") {
                                const checked = localSelectedMap[field.field_code]?.[0]?.value_text === "1";
                                return (
                                    <div key={field.labor_dimensions_id} className="mt-4">
                                        <label className="inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    setLocalSelectedMap((prev) => ({
                                                        ...prev,
                                                        [field.field_code]: [
                                                            {
                                                                value_code: field.field_code,
                                                                value_text: e.target.checked ? "1" : "0",
                                                            },
                                                        ],
                                                    }));
                                                }}
                                                className="mr-2"
                                            />
                                            {field.field_name}{" "}
                                            {(field?.required === "true" || field?.required === true) && <span className="text-red-500">*</span>}
                                        </label>
                                    </div>
                                );
                            }

                            if (field.field_type === "Text") {
                                return (
                                    <div key={field.labor_dimensions_id} className="mt-4">
                                        <label className="block font-semibold mb-1">
                                            {field.field_name}{" "}
                                            {(field?.required === "true" || field?.required === true) && <span className="text-red-500">*</span>}
                                        </label>
                                        <input
                                            type="text"
                                            value={localSelectedMap[field.field_code]?.[0]?.value_text || ""}
                                            onChange={(e) =>
                                                setLocalSelectedMap((prev) => ({
                                                    ...prev,
                                                    [field.field_code]: [
                                                        {
                                                            value_code: field.field_code,
                                                            value_text: e.target.value,
                                                        },
                                                    ],
                                                }))
                                            }
                                            className="w-full p-2 border border-gray-300 rounded-md"
                                            placeholder={`Enter ${field.field_name}`}
                                        />
                                    </div>
                                );
                            }

                            return null;
                        })}
                        </>
                    )}

                    <label className="text-gray-800 text-md font-medium mt-4">Note</label>
                    <textarea
                        rows={3}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        placeholder={getNoteLabel()}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    {/* Submit button */}
                    {submitting ? (
                        <div className="flex justify-center mt-6">
                            <Loader size={24} color="#2664eb" strokeWidth={2.5} className="animate-spin" />
                        </div>
                    ) : locationState?.mode === "edit" ? (
                        <div className="flex mt-6 space-x-2">
                            <button
                                type="button"
                                onClick={handleBackNavigation}
                                className="flex-1 py-2 bg-gray-400 text-white rounded-md font-semibold hover:bg-gray-500"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                                disabled={submitting}
                            >
                                Update
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="w-full mt-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                            disabled={submitting}
                        >
                            Submit
                        </button>
                    )}

                    {/* Bottom sheet for nested tag selection */}
                    <AttendanceTagSelector
                        isOpen={showBottomSheet}
                        field={fieldStack[fieldStack.length - 1] || null}
                        selected={localSelectedMap[fieldStack[0]?.field_code || ""] || []}
                        attendanceTagsOptions={eligibleAttendanceTags}
                        onClose={() => {
                            console.log("onClose called, setting showBottomSheet to false");
                            setShowBottomSheet(false);
                        }}
                        onSelect={handleTagOptionSelect}
                        fieldStack={fieldStack}
                        setFieldStack={setFieldStack}
                        localSelectedMap={localSelectedMap}
                        setLocalSelectedMap={setLocalSelectedMap}
                    />

                    {/* Break Manager Overlay */}
                    <BreakManagerOverlay
                        isOpen={showBreakManager}
                        breaks={breaks}
                        editIndex={editBreakIndex}
                        onClose={() => setShowBreakManager(false)}
                        onSave={(updatedBreaks) => {
                            setBreaks(updatedBreaks);
                            setShowBreakManager(false);
                            // toast.success(`${updatedBreaks.length} breaks saved`);
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default AddTimeSheetEntryComponent;
